import {Field, Formik, FormikHelpers as FormikActions} from 'formik';
import React from 'react';
import {Button} from '~/components/Button';
import {
    EditorField,
    Form,
    FormColumn,
    FormControls,
    FormRow,
    FormTitle,
    InputField,
    IntentText,
    NoteMessage,
    SelectField
} from '~/components/Form';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {Intent} from '~/lib/intent';
import {
    getSensitiveCategoryLabel,
    ISensitiveCategoryDeclaration,
    ISensitiveCategoryFormValues,
    SensitiveCategory,
    sensitiveCategories,
    sensitiveCategoryDecisions,
    SensitiveCategorySchema,
    toFormValues
} from '~/modules/Clients/models/SensitiveCategory';

export interface ISensitiveCategoryFormProps {
    category?: SensitiveCategory;
    declaration?: ISensitiveCategoryDeclaration;
    onSubmit?: (
        values: ISensitiveCategoryFormValues,
        formikActions: FormikActions<ISensitiveCategoryFormValues>,
        close: IModalComponentClose
    ) => void;
}

export const SensitiveCategoryForm: IModalComponent<ISensitiveCategoryFormProps> = props => {
    const category = props.category ?? sensitiveCategories[0].value;
    const close: IModalComponentClose = () => props.close?.();

    return <Formik
        initialValues={toFormValues(category, props.declaration)}
        validationSchema={SensitiveCategorySchema}
        onSubmit={(values, actions) => props.onSubmit?.(values, actions, close)}
    >
        {formik => {
            const {status, isSubmitting} = formik;

            return (
                <Form>
                    <FormTitle>{getSensitiveCategoryLabel(category)}</FormTitle>

                    {status && (
                        <IntentText intent={Intent.DANGER}>
                            {status}
                        </IntentText>
                    )}

                    <FormRow noBorder>
                        <FormColumn>
                            <NoteMessage>
                                Used to decide which private deals the inventory is eligible for.
                                It does not block this category in the open auction.
                            </NoteMessage>

                            <Field
                                name='decision'
                                label='Whole account'
                                options={{
                                    placeholder: 'Choose a decision',
                                    disabled: isSubmitting,
                                    items: sensitiveCategoryDecisions
                                }}
                                component={SelectField}
                            />

                            <Field
                                name='exceptionBundles'
                                label='Exceptions by app'
                                options={{
                                    mode: 'text',
                                    canCopy: true,
                                    disabled: isSubmitting,
                                    minLines: 3,
                                    maxLines: 12
                                }}
                                component={EditorField}
                            />
                            <NoteMessage>
                                One bundle per line. An exception takes the opposite decision from the whole account.
                            </NoteMessage>

                            <Field
                                name='note'
                                label='Note for the account team (optional)'
                                options={{
                                    placeholder: 'e.g. LDA-compliant creatives only',
                                    disabled: isSubmitting
                                }}
                                component={InputField}
                            />
                        </FormColumn>
                    </FormRow>

                    <FormControls>
                        <Button disabled={isSubmitting} onClick={close}>
                            Cancel
                        </Button>

                        <Button
                            type='submit'
                            disabled={isSubmitting}
                            intent={Intent.PRIMARY}
                            rightIcon={isSubmitting && <i className='fa fa-spinner fa-spin'/>}
                        >
                            Save
                        </Button>
                    </FormControls>
                </Form>
            );
        }}
    </Formik>;
};
